import { count_CONTRIBUTORS_IN_LAST_WEEK, count_WIKI_ARTICLE_EXPERIENCES } from 'mk/autogenerated/translations/utils.e93b75131ad0bfc03eb213315cba3cd1'
import { urlize } from 'mk/bazaar/common/filters';
import { italize } from 'mk/bazaar/common/filters/italize';
import { reactFilter, TextLike } from 'mk/bazaar/common/filters/utils';
import { wikiBoldize } from 'mk/bazaar/common/filters/wikiBoldize';
import { photoUrl } from 'mk/photo/photoUrl';
import { ArticleEntity, WikiCategoryEntity } from 'mk2/apps/wiki/schemas';
import * as wikiUrls from 'mk2/apps/wiki/urls';
import Img from 'mk2/components/Img';
import Link from 'mk2/components/Link';
import { url } from 'mk2/helpers/urls';
import { PhotoEntity } from 'mk2/schemas';
import { interpolate } from 'mk2/services/i18n';
import * as React from 'react';
import styles from './utils.mscss';

export const wikiArticleUrl = (article: ArticleEntity, hash?: string): string =>
    url(wikiUrls.wikiArticle, {articleSlug: article.slug, categorySlug: article.category.slug}, {}, hash);

export const wikiCategoryUrl = (category: WikiCategoryEntity): string =>
    url(wikiUrls.wikiArticle, {categorySlug: category.slug, articleSlug: category.categoryArticleSlug});

export const isCategoryArticle = (article: ArticleEntity): boolean =>
    article && article.id === article.category.categoryArticleId;

const WikiLink = ({ link, visibleText, count }) => {
        return (
            <Link to={link}>
                {visibleText}{count > 0 && (<sup>{count}<i className="fa fa-comment-o" /></sup>)}
            </Link>
        );
};

const OutsideLink = ({ link, visibleText, count, showContributorsCount}) => {
    return (
        <a href={link}>
            {visibleText}&nbsp;{showContributorsCount && (count > 0 ? (<span>•&nbsp;{interpolate(count_CONTRIBUTORS_IN_LAST_WEEK, {count})}</span>) : (<span>•&nbsp;{interpolate(count_CONTRIBUTORS_IN_LAST_WEEK, {count: 0})}</span>))}
        </a>
    );
};

export const linkProcessor = (lnk, visibleText, countExperiences, countTopicContributors, categorySlugs): React.ReactChild => {
    const INTERNAL_LINK_RE = /^\/?(.*?)\/?$/;
    const FORUM_TOPIC_SLUG_RE = /^https?:\/\/www.modrykonik.(?:sk|cz)\/forum\/category\/(.*?)\/?$/;
    const internalLinkMatcher = new RegExp(INTERNAL_LINK_RE, 'gm');
    const forumLinkMatcher = new RegExp(FORUM_TOPIC_SLUG_RE, 'gm');

    if (lnk.startsWith('/')) {
        const slug = internalLinkMatcher.exec(lnk);
        const linkWithCategory = '/' + categorySlugs[slug[1]] + lnk;
        return (
            <WikiLink
                link={linkWithCategory}
                visibleText={visibleText}
                count={countExperiences && countExperiences[slug[1]]}
            />
        );
    } else {
        const topicSlug = forumLinkMatcher.exec(lnk);
        return (
            <OutsideLink
                link={lnk}
                visibleText={visibleText}
                count={countTopicContributors && topicSlug && countTopicContributors[topicSlug[1]]}
                showContributorsCount={!!topicSlug}
            />
        );
    }
};

type LnkProcessorFunction = (srcLnk: string, visibleText: string) => React.ReactChild;

const WIKI_LINK_RE = /\[\[\s*(.*?)\s*\|\s*(.*?)\s*\]\]/;

// process mediawiki format links in the form of [[easygo-virage|EasyGo Virage]]
// using provided function which takes srcLnk and visibleText parameters and
// is expected to return React elements, e.g.:
// <Link to="/kocikopedia/easygo-virage/">EasyGo Virage</Link>
// this filter is used for our INTERNAL links
const wikiLinkize = (text: TextLike, lnkProcessor: LnkProcessorFunction) => reactFilter(text, (s: string): React.ReactChild[] => {
    const ret = [];
    const matcher = new RegExp(WIKI_LINK_RE, 'g');
    let from = 0;
    while (true) {
        const match = matcher.exec(s);
        if (match) {
            const matchLen = match[0].length;
            const lnk = match[1];
            const visibleText = match[2];

            const to = matcher.lastIndex - matchLen;

            // copy text before this link
            ret.push(s.substring(from, to));

            const finalLnk = lnkProcessor(lnk, visibleText);
            ret.push(finalLnk);

            from = matcher.lastIndex;
        } else {
            // copy text after last link
            ret.push(s.substring(from));
            break;
        }
    }
    return ret;
});

export const linkize = (text: TextLike, countExperiences: {[articleUrl: string]: number}, countTopicContributors: {[topicSlug: string]: number}, categorySlugs: {[articleSlug: string]: string}) =>
    wikiLinkize(text, (lnk, visibleText) => linkProcessor(lnk, visibleText, countExperiences, countTopicContributors, categorySlugs));

const HEADING_RE = /^==([^=].*?)$|^===([^=].*?)$|^====([^=].*?)$/;
export const replaceHeadingMarks = (text: TextLike, countExperiences: {[articleUrl: string]: number}, countTopicContributors: {[topicSlug: string]: number}, categorySlugs: {[articleSlug: string]: string}) =>
    reactFilter(text, (s: string): React.ReactChild[] => {
    const ret = [];
    const matcher = new RegExp(HEADING_RE, 'gm');
    let from = 0;

    while (true) {
        const match = matcher.exec(s);
        if (match) {
            const matchLen = match[0].length;
            const contentHeader1 = match[1];
            const contentHeader2 = match[2];
            const contentHeader3 = match[3];

            const to = matcher.lastIndex - matchLen;

            // copy text before this heading
            ret.push(s.substring(from, to));

            // insert heading content
            if (contentHeader1) {
                ret.push(<h2>{urlize(linkize(contentHeader1, countExperiences, countTopicContributors, categorySlugs), 50)}</h2>);
            } else if (contentHeader2) {
                ret.push(<h3>{urlize(linkize(contentHeader2, countExperiences, countTopicContributors, categorySlugs), 50)}</h3>);
            } else if (contentHeader3) {
                ret.push(<h4>{urlize(linkize(contentHeader3, countExperiences, countTopicContributors, categorySlugs), 50)}</h4>);
            }

            from = matcher.lastIndex;
        } else {
            // copy text after last link
            ret.push(s.substring(from));
            break;
        }
    }
    return ret;
});

const ORDERED_LIST_RE = /^(?:#[^\n].*(?:[\r\n]+|$))+/;
const ORDERED_LIST_ITEM_RE = /#(.*)/;

export const orderedList = (text: TextLike, countExperiences: {[articleUrl: string]: number}, countTopicContributors: {[topicSlug: string]: number}, categorySlugs: {[articleSlug: string]: string}) =>
    reactFilter(text, (s: string): React.ReactChild[] => {
    const ret = [];
    const listsMatcher = new RegExp(ORDERED_LIST_RE, 'gm');
    const listItemsMatcher = new RegExp(ORDERED_LIST_ITEM_RE);
    let from = 0;

    while (true) {
        const listMatch = listsMatcher.exec(s);
        if (listMatch) {
            const matchLen = listMatch[0].length;
            const to = listsMatcher.lastIndex - matchLen;
            const listItemsMatch = listMatch[0].match(/^#(.*)$/gm);

            // copy text before this list
            ret.push(s.substring(from, to));

            const listItems = [];
            for (let i = 0; i < listItemsMatch.length; i++) {
                const match = listItemsMatcher.exec(listItemsMatch[i]);
                const content = match[1];
                listItems.push(content);
            }

            // insert ordered list with li elements
            ret.push(
                <ol>
                    {listItems.map((item, index) => {
                        return (
                            <li key={index}>
                                {urlize(linkize(wikiBoldize(italize(item, countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), 50)}
                            </li>);
                        },
                    )}
                </ol>);

            from = listsMatcher.lastIndex;
        } else {
            // copy text after last list
            ret.push(s.substring(from));
            break;
        }
    }
    return ret;
});

const BULLET_LIST_RE = /^(?:(?:\*[^*\n].*?(?:[\r\n]{1,2}|$))+(?:^\*\*.*?(?:[\r\n]{1,2}|$))*)+/;
const BULLET_LIST_ITEM_RE = /(?:^\*\s*([^*].*))|(?:^\*\*\s*(.*))/;
export const bulletList = (text: TextLike, countExperiences: {[articleUrl: string]: number}, countTopicContributors: {[topicSlug: string]: number}, categorySlugs: {[articleSlug: string]: string}) =>
    reactFilter(text, (s: string): React.ReactChild[] => {
    const ret = [];
    const listsMatcher = new RegExp(BULLET_LIST_RE, 'gm');
    const listItemsMatcher = new RegExp(BULLET_LIST_ITEM_RE);
    let from = 0;

    while (true) {
        const listMatch = listsMatcher.exec(s);
        if (listMatch) {
            const matchLen = listMatch[0].length;
            const to = listsMatcher.lastIndex - matchLen;
            const listItemsMatch = listMatch[0].match(/(?:^\*[^*](.*)$)|(?:^\*\*(.*)$)/gm);

            // copy text before this list
            ret.push(s.substring(from, to));

            // create array of list items and nested items (sub-arrays)
            const items = [];
            for (let i = 0; i < listItemsMatch.length; i++) {
                const match = listItemsMatcher.exec(listItemsMatch[i]);
                if (match[1]) {
                    items.push([match[1]]);
                } else {
                    items[items.length - 1].push([match[2]]);
                }
            }

            // insert bullet list with li elements
            ret.push(
                <ul>
                    {items.map((item, index) => {
                        if (item.length === 1) {
                            return (
                                <li key={index}>
                                    {urlize(linkize(wikiBoldize(italize(item, countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), 50)}
                                </li>
                            );
                        }
                        if (item.length > 1) {
                            const lastBeforeNest = (
                                <li key={index}>
                                    {urlize(linkize(wikiBoldize(italize(item.shift(), countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), 50)}
                                </li>);
                            const nestedList = (
                                <ul>
                                    {item.map((nestedItem, i) => {
                                        return (
                                            <li key={i}>
                                                {urlize(linkize(wikiBoldize(italize(nestedItem, countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), 50)}
                                            </li>
                                        );
                                    })}
                                </ul>
                            );
                            return [lastBeforeNest, nestedList];
                        }
                    })
                    }
                </ul>);

            from = listsMatcher.lastIndex;
        } else {
            // copy text after last list
            ret.push(s.substring(from));
            break;
        }
    }
    return ret;
});

export const constructGiphy = (code: string, width: number, height: number) => {
    const src = `https://giphy.com/embed/${code}`;
    return (
        <iframe
            src={src}
            width={width}
            height={height}
            frameBorder="0"
            className="giphy-embed"
            allowFullScreen
        />
    );
};

export const constructInstagram = (code: string, height: number) => {
    const src = `https://www.instagram.com/p/${code}/embed/captioned/?cr=1&amp;v=7`;
    return (
        <iframe
            allowTransparency
            className="fr-instagram"
            frameBorder="0"
            height={height}
            scrolling="no"
            src={src}
            style={{
                background: '#ffffff', border: '1px solid #dbdbdb', borderRadius: '4px', display: 'block',
                margin: 0, maxWidth: '658px', padding: 0, width: '100%;',
            }}
        />
    );
};

export const constructVimeo = (code: string, width: number, height: number) => {
    const src = `https://player.vimeo.com/video/${code}`;
    return (
        <iframe
            frameBorder="0"
            height={height}
            src={src}
            width={width}
        />
    );
};

export const constructYoutube = (code: string, width: number, height: number) => {
    const src = `https://www.youtube.com/embed/${code}`;
    return (
        <iframe
            width={width}
            height={height}
            src={src}
            frameBorder="0"
            allowFullScreen
        />
    );
};

const YOUTUBE_RE = /<<youtube code="?\/?([\w-]+?)"?>>/;

export const wikiYoutubize = (text: TextLike) => reactFilter(text, (s: string): React.ReactChild[] => {
    const ret = [];
    const matcher = new RegExp(YOUTUBE_RE, 'gi');
    let from = 0;
    while (true) {
        const match = matcher.exec(s);
        if (match) {
            const matchLen = match[0].length;
            const code = match[1];

            const to = matcher.lastIndex - matchLen;

            // copy text before this video
            ret.push(s.substring(from, to));

            ret.push(constructYoutube(code, 560, 315));

            from = matcher.lastIndex;
        } else {
            // copy text after last video
            ret.push(s.substring(from));
            break;
        }
    }
    return ret;
});

export const constructImage = (photos: PhotoEntity[], className: string, cd: string, align: string, size: string, title: string) => {
    const photo = photos.find((p) => p.code === cd);
    if (!photo) {
        return [];
    }

    const version = size === 'small' ? 's246x246' :
                    size === 'medium' ? 's369x369' :
                    size === 'large' ? 's492x492' :
                    size === 'full' ? 's1600x1600' : null;
    let style;
    if (align === 'center') {
        style = {
            display: 'block',
            margin: '0 auto',
        };
    } else if (align === 'left') {
        style = {
            float: align as any,
            marginRight: 15,
        };
    } else {
        style = {
            float: align as any,
            marginLeft: 15,
        };
    }

    return (
        <Img
            src={photoUrl(photo, version)}
            className={className}
            style={style}
            width={photo.width}
            height={photo.height}
            title={title ? title : null}
            alt={title ? title : undefined}
        />
    );
};

const IMAGE_MARK_RE = /<<pic(.+?)>>/;
const IMAGE_CD_RE = /cd="([\w]+?)"/;
const IMAGE_ALIGN_RE = /align="([\w]+?)"/;
const IMAGE_SIZE_RE = /size="([\w]+?)"/;
const IMAGE_TITLE_RE = /title="(.+?)"/;

export const wikiReplaceImageMarks = (text: TextLike, photos: PhotoEntity[], className: string) => reactFilter(text, (s: string): React.ReactChild[] => {
    const ret = [];
    const matcher = new RegExp(IMAGE_MARK_RE, 'gi');
    const cdMatcher = new RegExp(IMAGE_CD_RE);
    const alignMatcher = new RegExp(IMAGE_ALIGN_RE);
    const sizeMatcher = new RegExp(IMAGE_SIZE_RE);
    const titleMatcher = new RegExp(IMAGE_TITLE_RE);

    let from = 0;
    while (true) {
        const match = matcher.exec(s);
        if (match) {
            const matchLen = match[0].length;
            const to = matcher.lastIndex - matchLen;
            const cd = cdMatcher.exec(match[1]);
            const align = alignMatcher.exec(match[1]);
            const size = sizeMatcher.exec(match[1]);
            const title = titleMatcher.exec(match[1]);

            // copy text before this mark
            ret.push(s.substring(from, to));

            ret.push(constructImage(photos, className, cd[1], align[1], size[1], title[1]));

            from = matcher.lastIndex;
        } else {
            ret.push(s.substring(from));
            break;
        }
    }
    return ret;
});

const TABLE_RE = /(?:^(?:\|[^\r\n|]*?)+(?:((\r\n)|\n|\r){1}|$))+/;
const TABLE_CELL_RE = /\|([^|[]*?(?:\[\[[^|]*?\|[^|]*?]][^|[]*?)*?)(?=\|)/;
const TABLE_HEAD_CELL_RE = /^\s?=\s?(.*)/;
export const replaceTableMarks = (
    text: TextLike, countExperiences: {[articleUrl: string]: number},
    countTopicContributors: {[topicSlug: string]: number},
    categorySlugs: {[articleSlug: string]: string},
    imageMarksReplacer: (bc: TextLike) => React.ReactChild[],
) =>
    reactFilter(text, (s: string): React.ReactChild[] => {
    const ret = [];
    const tableMatcher = new RegExp(TABLE_RE, 'gm');
    const tableCellMatcher = new RegExp(TABLE_CELL_RE, 'g');
    const tableHeadCellMatcher = new RegExp(TABLE_HEAD_CELL_RE);
    let from = 0;

    while (true) {
        const tableMatch = tableMatcher.exec(s);
        if (tableMatch) {
            const matchLen = tableMatch[0].length;
            const to = tableMatcher.lastIndex - matchLen;
            const tableRowsMatch = tableMatch[0].match(/(?:^(?:\|[^\r\n|]*)+(?:[\r\n]+|$))/gm);

            // copy text before this table
            ret.push(s.substring(from, to));

            // create array of cells in rows
            const cells = [];
            for (let i = 0; i < tableRowsMatch.length; i++) {
                cells.push([]);
                while (true) {
                    const cellMatch = tableCellMatcher.exec(tableRowsMatch[i]);
                    if (cellMatch) {
                        const content = cellMatch[1];
                        cells[i].push(content);
                    } else {
                        break;
                    }
                }
            }
            // render table- if cell starts with sign = add class header to the table cell
            ret.push(
                <div className={styles.utils__Table}>
                    <table>
                        {cells.map((cellsRow, index) => {
                            return (
                                <tbody key={index}>
                                    <tr>
                                        {cellsRow.map((cell, i) => {
                                            const title = tableHeadCellMatcher.exec(cell);
                                            if (title && (title[1] || title[1] === '')) {
                                               return (
                                                    <th key={i}>
                                                        {imageMarksReplacer(urlize(linkize(wikiBoldize(italize(title[1], countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), 50))}
                                                    </th>
                                                );
                                            } else {
                                                return (
                                                    <td key={i}>
                                                        {imageMarksReplacer(urlize(linkize(wikiBoldize(italize(cell, countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), countExperiences, countTopicContributors, categorySlugs), 50))}
                                                    </td>
                                                );
                                            }
                                        })}
                                    </tr>
                                </tbody>
                            );
                        })}
                    </table>
                </div>,
            );

            from = tableMatcher.lastIndex;
        } else {
            // copy text after last table
            ret.push(s.substring(from));
            break;
        }
    }
    return ret;
});

const HR_RE = /^----$/;

export const replaceHrMarks = (text: TextLike) =>
    reactFilter(text, (s: string): React.ReactChild[] => {
    const ret = [];
    const hrMatcher = new RegExp(HR_RE, 'gm');
    let from = 0;

    while (true) {
        const hrMatch = hrMatcher.exec(s);
        if (hrMatch) {
            const matchLen = hrMatch[0].length;
            const to = hrMatcher.lastIndex - matchLen;

            // copy text before this HR
            ret.push(s.substring(from, to));

            // insert HR
            ret.push(<hr/>);

            from = hrMatcher.lastIndex;
        } else {
            // copy text after last list
            ret.push(s.substring(from));
            break;
        }
    }
    return ret;
});

// urob z markdownu plaintext
export const poormanPlaintext = (markdownText: string): string =>
    markdownText
        .replace(new RegExp(IMAGE_MARK_RE, 'g'), '')
        .replace(new RegExp(WIKI_LINK_RE, 'g'), '$2')
        .replace(/(===|==|\*\*|\*|#)/g, '');
